import {
  Alert,
  Flex,
  Heading,
  RadioInput,
  RadioInputGroup,
  Text,
} from "@instructure/ui";
import { t } from "i18next";
import { useEffect, useState } from "react";
import DataGrid from "react-data-grid";
import Skeleton from "react-loading-skeleton";
import { useLocation, useOutletContext } from "react-router-dom";
import { Card } from "../../../../../components/Card";
import { useNotification } from "../../../../../components/CustomHooks/NotificationProvider";
import { AssignmentService } from "../../../../../services/AssignmentService";
import { buildLMGTable } from "../utils";
import DOMPurify from "dompurify";

const LearningMasteryGradebook = () => {
  const showNotification = useNotification();
  const { setNextEnabled, setFormData, formData } = useOutletContext();
  const location = useLocation();
  const section = location.state.section;

  const [roundingMethod, setRoundingMethod] = useState(
    formData.metadata?.roundingMethod || ""
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (formData.metadata.results && "roundingMethod" in formData.metadata) {
      const { outcomeColumns, studentRows } = buildLMGTable(
        formData.metadata.results,
        formData.metadata.roundingMethod
      );
      setColumns(outcomeColumns);
      setRows(studentRows);
    }
  }, [formData.metadata.results]); // eslint-disable-line

  useEffect(() => {
    async function fetchLMG() {
      try {
        setLoading(true);
        const results = await AssignmentService.getResultsByContext(
          AssignmentService.ROLLUP,
          section.canvas_id
        );
        const approved_los = new Set(
          section.learning_outcomes.map((outcome) =>
            DOMPurify.sanitize(outcome.statement)
          )
        );

        const approved_lo_results = results["data"].map((entry) => {
          const filteredScores = entry["scores"].filter((score) => {
            return approved_los.has(
              DOMPurify.sanitize(
                score["links"]["outcome_metadata"]["description"]
              )
            );
          });
          return { ...entry, scores: filteredScores };
        });
        results["data"] = approved_lo_results;
        setFormData((prev) => ({
          ...prev,
          metadata: { results: results, roundingMethod: "" },
        }));
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    }
    if (!formData.metadata?.results) {
      fetchLMG();
    }
  }, [section.canvas_id, showNotification, formData, setFormData]);

  function handleChangeRounding(_e, value) {
    setNextEnabled(true);
    setRoundingMethod(value);
    setFormData((prev) => ({
      ...prev,
      metadata: { ...prev.metadata, roundingMethod: value },
    }));

    const roundingMethod =
      value === "roundHalfUp" ? "_round_half_up" : "_round_down";

    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if (col.key === "student") return col;
        const outcomeId = col.key.split("_")[0];
        return { ...col, key: outcomeId + roundingMethod };
      })
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "0.75rem",
        height: "100%",
      }}
    >
      <Heading border="bottom" level="h3" as="h2" width="100%">
        {t("finalizeCourse.learningMasteryGradebook")}
      </Heading>
      {rows.length > 0 && columns.length > 1 ? (
        <>
          <Flex gap="large">
            <Flex.Item align="start">
              <RadioInputGroup
                name="roundingMethod"
                description={t("finalizeCourse.selectRoundingMethod")}
                value={roundingMethod}
                onChange={handleChangeRounding}
              >
                <RadioInput
                  label={t("finalizeCourse.roundHalfUp")}
                  value="roundHalfUp"
                />
                <RadioInput
                  label={t("finalizeCourse.roundDown")}
                  value="roundDown"
                />
              </RadioInputGroup>
            </Flex.Item>
            <Flex.Item shouldGrow shouldShrink>
              <Card
                style={{ backgroundColor: "#F2F8FA", border: "1px solid #ccc" }}
              >
                <Flex direction="column" gap="small">
                  <Flex.Item>
                    <Text weight="bold">{t("finalizeCourse.roundHalfUp")}</Text>
                    <Text>{t("finalizeCourse.roundHalfUpDescription")}</Text>
                  </Flex.Item>
                  <Flex.Item>
                    <Text weight="bold">{t("finalizeCourse.roundDown")}</Text>
                    <Text>{t("finalizeCourse.roundDownDescription")}</Text>
                  </Flex.Item>
                </Flex>
              </Card>
            </Flex.Item>
          </Flex>
          <DataGrid
            className="rdg-light"
            style={{ flex: "1", marginBottom: "0.05rem" }}
            rowHeight={60}
            headerRowHeight={40}
            columns={columns}
            rows={rows}
          />
        </>
      ) : loading ? (
        <div style={{ flex: "1" }}>
          <Skeleton style={{ marginBottom: "0.75rem" }} height={100} />
          <Skeleton height="100%" />
        </div>
      ) : error ? (
        <Alert variant="error">
          {t("notifications.general.fetchError", {
            obj: t("finalizeCourse.learningMasteryGradebook"),
          })}
        </Alert>
      ) : (
        <Alert>{t("finalizeCourse.noGradebookData")}</Alert>
      )}
    </div>
  );
};

export default LearningMasteryGradebook;
